import { DateTime } from 'luxon'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Grid,
  Typography,
  TextField,
  CircularProgress
} from '@material-ui/core'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import PropTypes from 'prop-types'
import { useState, useRef, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import AutoComplete from 'src/components/AutoComplete'
import { Matter } from 'src/types/matter'
import LoadingScreen from '../../LoadingScreen'
import { RootState } from '../../../store'
import {
  formatPersonsPayload,
  filterCommPersons,
  getDisplayNameId
} from '../../../utils/communications'
import {
  postKazooLiveNote,
  postLiveNote,
  removeConference,
  setSelectedNotes,
  updateKazooLiveNotes,
  updateLiveNotes
} from '../../../slices/phone'
import { CommUser, LiveNote } from '../../../types/communication'
import useAuth from '../../../hooks/useAuth'

const userOrContactMatch = (value, target) => {
  switch (value.type) {
    case 'User':
    case 'user':
      if (target.contactId) return false
      return value.id === target.australisId
    case 'Contact':
    case 'contact':
      if (target.contactId) return value.id === target.contactId
      return false
    default:
      return false
  }
}

const Notes = (props) => {
  const { call, closeNotes, type, onChangeText, onChangeField } = props
  const formRef = useRef(null)
  const dispatch = useDispatch()
  const { user } = useAuth()
  const { auth0Token, accountId, australisId } = user
  const posting = useSelector((state: RootState) => state.phone.loading.notes)
  const liveNotes = useSelector((state: RootState) => state.phone.liveNotes)
  const selectedNote = useSelector(
    (state: RootState) => state.phone.selectedNote
  )
  const loadingMatters = useSelector(
    (state: RootState) => state.matters.loading.matters
  )
  const matters = useSelector((state: RootState) => state.matters.matters)
  const contacts = useSelector((state: RootState) => state.contacts.contacts)
  const users = useSelector((state: RootState) => state.phone.australisUsers)

  const usersAndContacts = [
    ...users.map((u) => ({
      ...u,
      displayName: `${u.firstName} ${u.lastName} ||| ${u.australisId}`
    })),
    ...contacts.map((c) => ({
      ...c,
      displayName: `${c.contactDetails.firstName} ${c.contactDetails.lastName} ||| ${c.contactId}`
    }))
  ]

  const [senders, setSenders] = useState<any>([])
  const [receivers, setReceivers] = useState<any>([])
  const [notesExpanded, setNotesExpanded] = useState(false)
  const [justSaved, setJustSaved] = useState(false)
  const [startTime, setStartTime] = useState(null)

  const receiverRef = useRef(receivers)
  const senderRef = useRef(senders)

  const isKazooNote = type === 'kazoo'

  const existingNote = isKazooNote
    ? liveNotes[call?.interactionId]
    : liveNotes[call.conferenceSid]

  const getSenderOrReceiver = (persons) => {
    const res = (persons || [])
      .map((person) => {
        if (person.type === 'User') {
          return usersAndContacts.find((u) => {
            return u.australisId && u.australisId === person.id
          })
        }
        if (person.type === 'Contact') {
          if (person.id === '') {
            return person
          }
          return usersAndContacts.find((u) => {
            return u.contactId && u.contactId === person.id
          })
        }
        return null
      })
      .filter((a) => a !== null && a !== undefined)
    return res
  }

  const getTitle = (person: CommUser, contact) => {
    console.info(person)
    if (!contact) return person.phoneNumber
    const { name } = getDisplayNameId(contact)
    const callerIdNumber = name
      ? `(${existingNote.contactIdNumber})`
      : `${existingNote.contactIdNumber}`
    return `${name || ''} ${callerIdNumber}`
  }

  const getNoteTitle = () => {
    let title = `Call with `

    const isInbound = existingNote.direction === 'inbound'
    const isOutbound = existingNote.direction === 'outbound'

    if (
      (isInbound && !existingNote.sender.length) ||
      (isOutbound && !existingNote.receiver.length) ||
      (!isOutbound && !isInbound)
    ) {
      title += `${existingNote.contactIdNumber}`
      return title
    }

    if (isInbound) {
      const sender = existingNote.sender[0]
      const contactSender = getSenderOrReceiver([sender])[0]
      title += getTitle(sender, contactSender)
    } else {
      const receiver = existingNote.receiver[0]
      const contactReceiver = getSenderOrReceiver([receiver])[0]
      title += getTitle(receiver, contactReceiver)
    }

    return title
  }

  const getCurrentMatter = (selected) => {
    if (selected.matterId === '') return null
    return matters.find((m) => m.matterId === selected.matterId)
  }

  const getNoteParams = (e, matterVal: Matter) => {
    const params = {
      note: e.target.body.value,
      subject: e.target.subject.value,
      matterId: matterVal ? matterVal.matterId : '',
      sender: [],
      receiver: []
    }
    if (isKazooNote) {
      params.sender = formatPersonsPayload(senders, call.sender[0])
      params.receiver = formatPersonsPayload(receivers, call.receiver[0])
      return params
    }

    params.sender = formatPersonsPayload(senders)
    params.receiver = formatPersonsPayload(receivers)
    return params
  }

  const handlePostCommunication = async (event: any) => {
    event.preventDefault()

    const forms = document.forms.length && document.forms[0]
    let matterValue = ''

    if (forms) {
      const elements = forms.querySelector('[data-id="Matter"]')
      if (elements) {
        const inputs = elements.getElementsByTagName('input')
        if (inputs.length && inputs[0].value) matterValue = inputs[0].value
      } else {
        matterValue = formRef?.current?.matter?.value
      }
    }

    const m = matters.find(
      (matter) =>
        `${matter.displayName} - ${matter.externalMatterIds.clio}` ===
        matterValue
    )

    const params = getNoteParams(event, m)
    if (isKazooNote) {
      dispatch(
        postKazooLiveNote(auth0Token, accountId, australisId, call, params)
      )

      if (['completed'].includes(call.conf_status))
        closeNotes(call.interactionId, true)
      else dispatch(updateKazooLiveNotes({ ...existingNote, saved: true }))
    } else {
      dispatch(
        postLiveNote(auth0Token, accountId, australisId, call.name, params)
      )
      dispatch(updateLiveNotes({ ...existingNote, ...params, saved: true }))

      if (['completed', 'abandoned', 'missed'].includes(call.conf_status))
        dispatch(removeConference(call.conferenceSid))
    }
  }

  const handleClose = (e, noteObj) => {
    e.preventDefault()
    if (noteObj && noteObj.interactionId) {
      closeNotes(noteObj.interactionId, noteObj.isSession)
    } else {
      closeNotes()
    }
  }

  const handleChange = (e) => {
    if (!isKazooNote) return
    const { name, value } = e.target
    dispatch(setSelectedNotes(existingNote.interactionId))
    onChangeText(selectedNote, value, name)
  }

  const handleChangeField = (value: string, label: string) => {
    const isSender = label === 'From'
    const isReceiver = label === 'To'

    if (isSender) setSenders(value)
    else if (isReceiver) setReceivers(value)

    if (isKazooNote) {
      dispatch(setSelectedNotes(existingNote.interactionId))
      onChangeField(selectedNote, value, label)
    }
  }

  useEffect(() => {
    if (isKazooNote) return
    receiverRef.current = receivers
    senderRef.current = senders
  }, [receivers, senders])

  useEffect(() => {
    const myData = isKazooNote ? call : call.userData[user.australisId]
    if (myData && myData.receiver && myData.sender) {
      if (receiverRef.current.length === 0 && myData.receiver.length > 0) {
        setReceivers(
          usersAndContacts.filter(
            (i) => !!myData.receiver.find((r) => userOrContactMatch(r, i))
          )
        )
      }
      if (senderRef.current.length === 0 && myData.sender.length > 0) {
        setSenders(
          usersAndContacts.filter(
            (i) => !!myData.sender.find((s) => userOrContactMatch(s, i))
          )
        )
      }
      if (
        myData.matterId &&
        myData.matterId !== 'Unknown' &&
        !isKazooNote &&
        formRef.current &&
        !formRef.current?.matter?.value
      ) {
        formRef.current.matter.value =
          matters.find((matter) => matter.matterId === myData.matterId)
            ?.displayName || ''
      }
    }
    return () => {
      if (isKazooNote) {
        setReceivers([])
        setSenders([])
      }
    }
  }, [call])

  useEffect(() => {
    if (posting === false && call?.conf_status === 'inProgress') {
      setJustSaved(true)

      setTimeout(() => {
        setJustSaved(false)
      }, 2000)
    }
  }, [posting])

  useEffect(() => {
    if (!isKazooNote) return
    if (existingNote) setNotesExpanded(true)
    if (!existingNote.startTime && !existingNote.dateCreated) {
      setStartTime(0)
    }
    if (existingNote.startTime || existingNote.dateCreated) {
      const start = existingNote.startTime || Number(existingNote.dateCreated)
      const dateString = DateTime.fromMillis(start)
        .toLocal()
        .toLocaleString(DateTime.DATETIME_SHORT)
      setStartTime(dateString)
    }
  }, [isKazooNote, existingNote])

  useEffect(() => {
    if (isKazooNote) return
    const myData = call.userData[user.australisId]
    if (call.conf_status === 'completed') {
      dispatch(removeConference(call.conferenceSid))
    }
    const m = matters.find(
      (matter) =>
        `${matter.displayName} - ${matter.externalMatterIds.clio}` ===
        formRef?.current?.matter?.value
    )

    const params: LiveNote = {
      note: existingNote ? existingNote.note : '',
      subject: existingNote ? existingNote.subject : '',
      matterId: m ? m.matterId : '',
      sender: formatPersonsPayload(senderRef.current),
      receiver: formatPersonsPayload(receiverRef.current)
    }

    if (!existingNote) {
      params.note = myData ? myData.note : ''
      params.subject = myData ? myData.subject : ''
      params.sender = myData ? myData.sender : []
      params.receiver = myData ? myData.receiver : []
    }

    dispatch(
      updateLiveNotes({
        conferenceSid: call.conferenceSid,
        ...params
      })
    )
  }, [call])

  //   console.log('senderRef.current', senderRef.current)
  //   console.log('receiverRef.current', receiverRef.current)
  //   console.log('call', call)
  //   console.log('existingNote', existingNote)
  //   console.log('startTime', startTime)

  if (loadingMatters) {
    return (
      <Box
        sx={{
          backgroundColor: 'background.default',
          minHeight: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          py: 8
        }}
      >
        <LoadingScreen />
      </Box>
    )
  }

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          pl: 1,
          pb: 1
        }}
      >
        <form onSubmit={handlePostCommunication} ref={formRef}>
          <Grid container spacing={2}>
            <Accordion sx={{ width: '100%' }} expanded={notesExpanded}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                onClick={() => setNotesExpanded(!notesExpanded)}
              >
                {isKazooNote ? (
                  <Typography>{getNoteTitle() || 'Unknown'}</Typography>
                ) : (
                  <Typography>Matter Options…</Typography>
                )}
              </AccordionSummary>

              <AccordionDetails>
                {existingNote && !!Object.keys(existingNote).length && (
                  <Grid container spacing={2}>
                    <Grid
                      item
                      md={12}
                      xs={12}
                      sx={{
                        display: isKazooNote && startTime ? 'block' : 'none',
                        marginBottom: '1rem'
                      }}
                    >
                      <TextField
                        label="Time/Date"
                        value={startTime}
                        variant="standard"
                        InputProps={{ disableUnderline: true, readOnly: true }}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <AutoComplete
                      options={usersAndContacts || []}
                      defaultValue={
                        !isKazooNote && existingNote
                          ? getSenderOrReceiver(existingNote.sender)
                          : null
                      }
                      value={
                        isKazooNote && existingNote
                          ? getSenderOrReceiver(existingNote.sender)
                          : undefined
                      }
                      filterOptions={filterCommPersons}
                      onChange={handleChangeField}
                      label="From"
                      md={12}
                    />
                    <AutoComplete
                      options={usersAndContacts || []}
                      defaultValue={
                        !isKazooNote && existingNote
                          ? getSenderOrReceiver(existingNote.receiver)
                          : null
                      }
                      value={
                        isKazooNote && existingNote
                          ? getSenderOrReceiver(existingNote.receiver)
                          : undefined
                      }
                      filterOptions={filterCommPersons}
                      onChange={handleChangeField}
                      label="To"
                      md={12}
                    />
                    <AutoComplete
                      options={matters}
                      defaultValue={
                        !isKazooNote && existingNote
                          ? getCurrentMatter(existingNote)
                          : null
                      }
                      value={
                        isKazooNote && existingNote
                          ? getCurrentMatter(existingNote)
                          : undefined
                      }
                      onChange={handleChangeField}
                      label="Matter"
                      md={12}
                    />
                  </Grid>
                )}
              </AccordionDetails>
            </Accordion>
            <Grid
              item={!!!isKazooNote}
              md={12}
              xs={12}
              sx={{
                paddingLeft: '16px',
                paddingRight: '16px'
              }}
            >
              <TextField
                fullWidth
                label="Subject"
                name="subject"
                defaultValue={
                  !isKazooNote && existingNote ? existingNote.subject : null
                }
                value={
                  isKazooNote && existingNote ? existingNote.subject : undefined
                }
                onChange={(e) => handleChange(e)}
                required
                variant="outlined"
              />
            </Grid>
            <Grid item md={12} xs={12} sx={{ paddingRight: '16px' }}>
              <TextField
                required={!!isKazooNote}
                multiline
                minRows={
                  isKazooNote && notesExpanded
                    ? 19
                    : isKazooNote && !notesExpanded
                    ? 30
                    : notesExpanded
                    ? 4
                    : 16
                }
                maxRows={30}
                placeholder="Insert subject body..."
                variant="outlined"
                defaultValue={
                  !isKazooNote && existingNote ? existingNote.note : null
                }
                value={
                  isKazooNote && existingNote ? existingNote.note : undefined
                }
                fullWidth
                label="Body"
                name="body"
                onChange={(e) => handleChange(e)}
              />
            </Grid>
            <Grid
              item
              xs={6}
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
              }}
            >
              <Button
                onClick={(e) => handleClose(e, existingNote)}
                color="primary"
              >
                Close
              </Button>
            </Grid>
            <Grid
              item
              xs={6}
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                paddingRight: '16px'
              }}
            >
              <Button
                variant="contained"
                type="submit"
                color="primary"
                disabled={posting}
                sx={{
                  width:
                    existingNote?.conf_status === 'completed' ? '100%' : '100px'
                }}
              >
                {call.conf_status === 'completed' && 'Save & Close'}
                {call.conf_status !== 'completed' &&
                  !posting &&
                  !justSaved &&
                  'Save'}
                {call.conf_status !== 'completed' && posting && (
                  <>
                    <CircularProgress size="0.875rem" sx={{ mr: 1 }} />
                    <span>Saving</span>
                  </>
                )}
                {call.conf_status !== 'completed' &&
                  !posting &&
                  justSaved &&
                  'Saved!'}
              </Button>
            </Grid>
          </Grid>
        </form>
      </Box>
    </>
  )
}

Notes.propTypes = {
  call: PropTypes.any,
  closeNotes: PropTypes.func,
  onChangeText: PropTypes.func,
  onChangeField: PropTypes.func,
  type: PropTypes.string
}

export default Notes
